import * as fs from "node:fs";
import * as path from "node:path";

export const SEED_MANIFEST_PATH = ".m8t/seed-manifest.json";

export const SEED_PATHS = ["skills", "references", "targets"] as const;

/** Where a seed's content came from, when it came from a pinned agent repo. */
export interface SeedUpstream {
  /** "owner/name" of the agent repo, verbatim from the release pin. */
  repo: string;
  /** The pinned commit of that repo — an agent-repo rev, not a platform one. */
  ref: string;
}

export interface SeedLineage {
  name: string;
  /** A real, fetchable platform commit — the rev this repo's seed was last brought to. */
  refreshedToCommit: string;
  refreshedToVersion: string;
  /** Repo-relative seed-owned paths (incl. SEED_MANIFEST_PATH itself). The discriminator. */
  ownedPaths: string[];
  /**
   * The agent repo + commit this seed's content came from, when it came from one.
   *
   * `refreshedToCommit` is a PLATFORM commit and cannot address an agent repo's
   * history, so once a seed is sourced from a pinned agent repo there is no
   * reachable merge base and a refresh falls back to add-only. This records the
   * rev that WOULD serve as that base.
   *
   * Nothing reads it yet — downstream updates are parked. It is written now
   * because a base that was never recorded cannot be reconstructed afterwards:
   * every brain created without it is permanently baseless.
   *
   * OPTIONAL, and absence is valid forever: every brain that predates this field
   * has no upstream, and their markers must keep parsing exactly as before.
   */
  upstream?: SeedUpstream;
}

export interface SeedManifest {
  schemaVersion: 1;
  seeds: SeedLineage[];
}

/** Absent is valid; present must be COMPLETE. Half a provenance is worse than
 *  none — it reads as a usable base and resolves to nothing. */
function isUpstream(v: unknown): v is SeedUpstream {
  if (v === undefined) return true;
  if (!v || typeof v !== "object") return false;
  const u = v as Record<string, unknown>;
  return typeof u.repo === "string" && u.repo.length > 0
    && typeof u.ref === "string" && u.ref.length > 0;
}

function isLineage(v: unknown): v is SeedLineage {
  if (!v || typeof v !== "object") return false;
  const l = v as Record<string, unknown>;
  return typeof l.name === "string" && typeof l.refreshedToCommit === "string"
    && typeof l.refreshedToVersion === "string" && Array.isArray(l.ownedPaths)
    && l.ownedPaths.every((p) => typeof p === "string")
    && isUpstream(l.upstream);
}

export function parseSeedManifest(text: string | null): SeedManifest | null {
  if (!text?.trim()) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { return null; }
  if (!parsed || typeof parsed !== "object") return null;
  const m = parsed as Record<string, unknown>;
  if (m.schemaVersion !== 1 || !Array.isArray(m.seeds) || !m.seeds.every(isLineage)) return null;
  return { schemaVersion: 1, seeds: m.seeds };
}

export function buildSeedManifest(seeds: SeedLineage[]): SeedManifest {
  return { schemaVersion: 1, seeds };
}

/**
 * Give a rebuilt lineage the provenance the prior one recorded.
 *
 * `upsertLineage` replaces a same-name entry WHOLESALE, and the seed-refresh rail
 * rebuilds the lineage from scratch on every delivery. Without this, `brain create`
 * records where the seed came from and the very first `platform converge` erases
 * it — which defeats the whole reason it is written at creation time.
 *
 * The rebuilt lineage wins if it already knows its own provenance: a refresh that
 * genuinely delivered from a newer agent ref should record THAT, not the older one.
 * Carried per seed NAME — one seed's provenance is never another's.
 */
export function carryUpstream(prior: SeedManifest | null, lineage: SeedLineage): SeedLineage {
  if (lineage.upstream) return lineage;
  const before = prior?.seeds.find((s) => s.name === lineage.name)?.upstream;
  return before ? { ...lineage, upstream: before } : lineage;
}

export function upsertLineage(manifest: SeedManifest | null, lineage: SeedLineage): SeedManifest {
  const prior = manifest?.seeds ?? [];
  const others = prior.filter((s) => s.name !== lineage.name);
  return { schemaVersion: 1, seeds: [...others, lineage].sort((a, b) => a.name.localeCompare(b.name)) };
}

export function serializeSeedManifest(m: SeedManifest): string {
  return JSON.stringify(m, null, 2) + "\n";
}

function walkFiles(root: string, rel = ""): string[] {
  const abs = path.join(root, rel);
  if (!fs.existsSync(abs)) return [];
  const out: string[] = [];
  for (const entry of fs.readdirSync(abs, { withFileTypes: true })) {
    const childRel = rel ? `${rel}/${entry.name}` : entry.name;
    if (entry.isDirectory()) out.push(...walkFiles(root, childRel));
    else if (entry.isFile()) out.push(childRel);
  }
  return out;
}

/** Repo-relative seed-owned paths under the manifest's SEED_PATHS subtrees, plus the marker.
 *  Structurally cannot include memory/ etc. — it only walks SEED_PATHS. */
export function deriveOwnedPaths(seedDir: string): string[] {
  const owned = new Set<string>([SEED_MANIFEST_PATH]);
  for (const sub of SEED_PATHS) {
    for (const rel of walkFiles(path.join(seedDir, sub))) owned.add(`${sub}/${rel}`);
  }
  return [...owned].sort((a, b) => a.localeCompare(b));
}

export function isSeedOwned(repoRelPath: string, ownedPaths: string[]): boolean {
  return ownedPaths.includes(repoRelPath);
}
